import React from "react";
import Form from "components/composite/Form";
import disposableDomains from "disposable-email-domains";
import freeEmailProviders from "email-providers/common.json";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const freeEmailDomainSet = new Set(freeEmailProviders);

const disposableDomainSet = new Set(disposableDomains);

const mxLookupCache = new Map();

const extractDomain = (email) => {
  const at = email.lastIndexOf("@");
  if (at === -1) return null;
  return email.slice(at + 1).toLowerCase().trim();
};

const hasMxRecord = async (domain) => {
  if (mxLookupCache.has(domain)) return mxLookupCache.get(domain);

  const lookup = (async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      const response = await fetch(
        `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(
          domain
        )}&type=MX`,
        {
          headers: { accept: "application/dns-json" },
          signal: controller.signal,
        }
      );
      clearTimeout(timeout);
      if (!response.ok) return true;
      const data = await response.json();
      // Status 0 = NOERROR. Answer present with MX records = deliverable.
      if (data.Status !== 0) return false;
      return Array.isArray(data.Answer) && data.Answer.length > 0;
    } catch {
      // Network failure / timeout / CORS — fail open so we never block a real customer.
      return true;
    }
  })();

  mxLookupCache.set(domain, lookup);
  return lookup;
};

const RevealQuickAnalysisForm = ({
  handleSubmit,
  validateOnChange,
  submitText,
}) => {
  const { t } = useTranslation(["analyse", "forms"]);

  const fields = [
    {
      id: "firstName",
      value: "",
      label: t("forms:fields.firstName.label"),
      placeholder: t("forms:fields.firstName.placeholder"),
      required: true,
    },
    {
      id: "lastName",
      value: "",
      label: t("forms:fields.lastName.label"),
      placeholder: t("forms:fields.lastName.placeholder"),
      required: true,
    },
    {
      fullSize: true,
      id: "companyEmail",
      value: "",
      label: t("forms:fields.companyEmail.label"),
      placeholder: t("forms:fields.companyEmail.placeholder"),
      required: true,
      type: "email",
    },
  ];

  const validations = {
    firstName: yup
      .string(t("forms:validation.firstName"))
      .required(t("forms:validation.firstName")),
    lastName: yup
      .string(t("forms:validation.lastName"))
      .required(t("forms:validation.lastName")),
    companyEmail: yup
      .string(t("forms:validation.email"))
      .email(t("forms:validation.email"))
      .required(t("forms:validation.email"))
      .test(
        "not-free-or-disposable",
        t("analyse:reveal.emailValidation"),
        (value) => {
          const domain = value && extractDomain(value);
          if (!domain) return true;
          return (
            !freeEmailDomainSet.has(domain) && !disposableDomainSet.has(domain)
          );
        }
      )
      .test(
        "domain-has-mx",
        t("analyse:reveal.emailDomainUnknown"),
        async (value) => {
          const domain = value && extractDomain(value);
          if (!domain) return true;
          if (
            freeEmailDomainSet.has(domain) ||
            disposableDomainSet.has(domain)
          ) {
            return true;
          }
          return hasMxRecord(domain);
        }
      ),
  };

  return (
    <div>
      <Form
        className="py-4"
        fields={fields}
        nativeSubmit={false}
        onSubmit={handleSubmit}
        submitText={submitText}
        validations={validations}
        validateOnChange={validateOnChange}
        wrap={false}
        gap={0.5}
        inheritFont={true}
      ></Form>
    </div>
  );
};

export default RevealQuickAnalysisForm;
